import type { ChangeEvent } from 'react';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Input from 'creative-ui/uikit/Input';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Flex } from '@/components/Layout/Flex/Flex';
import { trackClick } from '@/lib/tagging';

import { useMfaSmsEnrollmentManager } from '../hooks/useMfaSmsEnrollmentManager';

const PHONE_FILTER_PATTERN = /[^0-9+]/g;

interface MfaSmsEnrollmentFormProps {
  fieldErrors: Record<string, string>;
}

interface FormData {
  phone: string;
}

function MfaSmsEnrollmentForm({
  fieldErrors,
}: Readonly<MfaSmsEnrollmentFormProps>) {
  const { t } = useTranslation('mfaSmsEnrollment');
  const { handleContinueEnrollment, texts, data } =
    useMfaSmsEnrollmentManager();

  const form = useForm<FormData>({
    defaultValues: {
      phone: (data?.phone as string) ?? '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  // SDK first, i18n fallback
  const phoneLabel = texts?.placeholder || t('form.phoneLabel');
  const buttonText = texts?.buttonText || t('form.continueButtonText');

  // i18n only (validation/UX strings)
  const phonePlaceholder = t('form.phonePlaceholder');
  const sendingText = t('form.sendingText');
  const phoneRequiredError = t('errors.phoneRequired');

  // Get SDK errors for phone field
  const phoneSDKError = fieldErrors.phone;

  const onSubmit = async (formData: FormData) => {
    trackClick('mfa/sms/continue');
    await handleContinueEnrollment(formData.phone);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gapY={24}>
        <Controller
          name="phone"
          control={control}
          rules={{
            required: phoneRequiredError,
          }}
          render={({ field }) => (
            <Input
              {...field}
              name="phone"
              type="tel"
              inputMode="tel"
              placeholder={phonePlaceholder}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                field.onChange(
                  event.target.value.replace(PHONE_FILTER_PATTERN, ''),
                );
              }}
              status={errors.phone || phoneSDKError ? 'error' : undefined}
              statusMessage={errors.phone?.message ?? phoneSDKError}>
              {phoneLabel}
            </Input>
          )}
        />

        <CtaContainer stack>
          <Cta
            className="c-submitCta"
            type="primary"
            submit
            disabled={isSubmitting}
            isWaiting={isSubmitting}>
            {isSubmitting ? sendingText : buttonText}
          </Cta>
        </CtaContainer>
      </Flex>
    </form>
  );
}

export default MfaSmsEnrollmentForm;
